import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './ReportModal.css';

// --- Icon Imports ---
import {
    X, User, Wallet, Briefcase, BarChart, Shield, Link as LinkIcon,
    ListChecks, LineChart, CheckCircle, ChevronDown, FileText,
    MessageSquare, HelpCircle, Home, Utensils, Car, Coins,
    Award, Calendar, Flag, BookOpen, TrendingUp, Users, Plane, Banknote
} from 'lucide-react';

// --- Image Imports (ensure paths are correct) ---
import studentImage from '../../assets/images/student.jpg';
import logo from '../../assets/images/whitelogo.png';


// ===================================================================================
//  HELPER COMPONENTS TO KEEP THE UI CLEAN
// ===================================================================================

// A generic card component for consistent styling
const InfoCard = ({ title, icon, children, className = '' }) => (
    <div className={`rm-card ${className}`}>
        <h3 className="rm-card-title">{icon}{title}</h3>
        <div className="rm-card-content">{children}</div>
    </div>
);

// A key-value pair for displaying details
const DetailItem = ({ label, value, children }) => {
    if (!value && !children) return null;
    return (
        <div className="rm-detail-item">
            <strong className="rm-detail-label">{label}</strong>
            <span className="rm-detail-value">{value || children}</span>
        </div>
    );
};

// A styled list item for checklists and suggestions
const ListItem = ({ children, icon = <CheckCircle size={14} /> }) => (
    <li className="rm-list-item">{icon}{children}</li>
);

// Accordion for Q&A
const QnAItem = ({ item }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="rm-qna-item">
            <p className="rm-qna-question" onClick={() => setIsOpen(!isOpen)}>
                {item.question}
                <ChevronDown className={`rm-qna-chevron ${isOpen ? 'rm-open' : ''}`} size={20} />
            </p>
            {isOpen && <p className="rm-qna-answer">{item.answer}</p>}
        </div>
    );
};


// ===================================================================================
//  MAIN REPORT MODAL COMPONENT
// ===================================================================================
const ReportModal = ({ report, onClose }) => {
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        const mountTimer = setTimeout(() => setIsMounted(true), 50);
        return () => clearTimeout(mountTimer);
    }, []);

    // --- Early exit if core data is missing ---
    if (!report?.aiResults || !report?.formDataSnapshot) {
        return (
            <div className="rm-modal-overlay" onClick={onClose}>
                <div className="rm-modal-content" onClick={(e) => e.stopPropagation()}>
                    <InfoCard title="Error" icon={<HelpCircle />}>
                        <p>Report data is incomplete and cannot be displayed.</p>
                        <button className="close-modal-button" onClick={onClose}>Close</button>
                    </InfoCard>
                </div>
            </div>
        );
    }
    

    // --- Destructure ALL data from the report object for easy access ---
    const { formDataSnapshot, aiResults } = report;
    const {
        collegeAnalysis = {},
        jobMarketSnapshot = {},
        visaAndSOPAdvice = {},
        educationCostBreakdown = {},
        costOfLiving = {},
        prPathwayRoadmap = {},
        finalActionPlan = {},
        backupCourseCountry = {},
        predictedQnA = [],
        finalComment = '',
        footerResources = {},
        additionalCertifications = [],
        scholarshipDetails = [],
        timelineInvestmentPlanning = {},
        postStudyWorkAndPR = {}
    } = aiResults;

    const studentFullName = `${formDataSnapshot.firstName || ''} ${formDataSnapshot.lastName || ''}`.trim();

      // --- Helper Variable for cleaner access ---
    const specificReport = collegeAnalysis.specificCollegeReport || {};

    // --- Helper Function for safe date formatting ---
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid Date';
            return date.toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric'
            });
        } catch (error) {
            return 'N/A';
        }
    };

    return (
        <div className="rm-modal-overlay" onClick={onClose}>
            <Helmet>
                <title>{`Report for ${studentFullName || 'Student'} | GN VISA Admin`}</title>
            </Helmet>
            <div className="rm-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className={`rm-modal-main-container ${isMounted ? 'rm-mounted' : ''}`}>
                    <header className="rm-page-header">
                        <img className="rm-brand-logo" src={logo} alt="Logo" />
                        <p className="rm-title">Personalized Immigration & Education Report</p>
                        <button className="rm-close-button-main" onClick={onClose}><X size={24} /></button>
                    </header>

                    <div className="rm-report-layout">
                        {/* ======================================================= */}
                        {/*                     LEFT SIDEBAR                        */}
                        {/*        (Focus on Student Profile & Input Data)          */}
                        {/* ======================================================= */}
                        <aside className="rm-left-sidebar">
                            <InfoCard title="Student Profile" icon={<User size={16} />} className="rm-profile-card">
                                <div className="rm-student-profile-header">
                                    <img src={studentImage} alt={studentFullName} />
                                    <div>
                                        <h3>{studentFullName}</h3>
                                        <p className="rm-goal-text">Goal: {formDataSnapshot.goalCountry}</p>
                                    </div>
                                </div>
                                <DetailItem label="Course" value={formDataSnapshot.collegeCourse} />
                                <DetailItem label="Background" value={`${formDataSnapshot.stream12th} (${formDataSnapshot.percentage12th}%)`} />
                                <DetailItem label="College %" value={formDataSnapshot.collegePercentage} />
                                <DetailItem label="TOEFL Score" value={formDataSnapshot.toeflScore} />
                            </InfoCard>

                            <InfoCard title="Financial Snapshot" icon={<Banknote size={16} />}>
                                <DetailItem label="Budget" value={formDataSnapshot.budget} />
                                <DetailItem label="Bank (Liquid)" value={formDataSnapshot.bankBalance?.liquid} />
                                <DetailItem label="Bank (FD)" value={formDataSnapshot.bankBalance?.fd} />
                                <DetailItem label="Father's Salary" value={formDataSnapshot.father?.salary} />
                            </InfoCard>

                            <InfoCard title="Visa & SOP Analysis" icon={<FileText size={16} />}>
                                <DetailItem label="Visa Type" value={visaAndSOPAdvice.visaType} />
                                <DetailItem label="Rejection Risk" value={visaAndSOPAdvice.rejectionRiskPercentReasons} />
                                <h4 className="rm-sub-heading">Document Checklist</h4>
                                <ul>{visaAndSOPAdvice.documentChecklist?.map(doc => <ListItem key={doc}>{doc}</ListItem>)}</ul>
                                <h4 className="rm-sub-heading">SOP Suggestions</h4>
                                <ul>{visaAndSOPAdvice.sopSuggestions?.map((sugg, i) => <ListItem key={i}>{sugg}</ListItem>)}</ul>
                            </InfoCard>
                        </aside>

                        {/* ======================================================= */}
                        {/*                      MAIN CONTENT                       */}
                        {/*    (Focus on the Core Recommendation and Analysis)      */}
                        {/* ======================================================= */}
                        <main className="rm-main-content">
                            <div className="rm-scroll-wrapper"> {/* Ensures this column is scrollable */}

                                <InfoCard title="Primary Recommendation" icon={<Award size={18} />} className="rm-college-card">
                                    <h3>{formDataSnapshot.selectedCollege}</h3>
                                    <p className="rm-course-highlight">{specificReport.courseName}</p>
                                    <div className="rm-college-info-grid">
                                        <DetailItem label="Location" value={`${formDataSnapshot.goalState}, ${formDataSnapshot.goalCountry}`} />
                                        <DetailItem label="Duration" value={specificReport.duration} />
                                        <DetailItem label="Intake" value={specificReport.intake} />
                                        <DetailItem label="Success Probability" value={specificReport.successProbabilityPercent ? `${specificReport.successProbabilityPercent}%` : 'N/A'} />
                                        <DetailItem label="Tuition" value={`${specificReport.tuitionFee?.local} (${specificReport.tuitionFee?.inr})`} />
                                        <DetailItem label="Application Deadline" value={formatDate(specificReport.applicationDeadline)} />
                                    </div>
                                    <DetailItem label="Entry Requirements">{specificReport.entryRequirements}</DetailItem>
                                </InfoCard>

                                <InfoCard title="College Analysis" icon={<BookOpen size={16} />}>
                                    <DetailItem label="Ranking & Reputation" value={collegeAnalysis.rankingAndReputation} />
                                    <DetailItem label="Accreditation" value={collegeAnalysis.accreditationRecognition} />
                                    <DetailItem label="Curriculum Summary" value={specificReport.courseCurriculumSummary} />
                                </InfoCard>

                                {jobMarketSnapshot.roles?.length > 0 && (
                                    <InfoCard title="Career & Job Outlook" icon={<Briefcase size={16} />}>
                                        <h4 className="rm-sub-heading">Potential Full-Time Roles</h4>
                                        {jobMarketSnapshot.roles.map(role => (
                                            <DetailItem key={role.title} label={role.title}>
                                                {role.salary?.local} ({role.salary?.inr}) - <em>Demand: {role.demandLevel}</em>
                                            </DetailItem>
                                        ))}

                                        {jobMarketSnapshot.partTimeOpportunities?.length > 0 && (
                                            <>
                                                <h4 className="rm-sub-heading">Part-Time Opportunities</h4>
                                                {jobMarketSnapshot.partTimeOpportunities.map(job => (
                                                    <DetailItem key={job.role} label={job.role}>
                                                        {job.wage?.local}/hr - <em>Finding Difficulty: {job.findingDifficulty}</em>
                                                    </DetailItem>
                                                ))}
                                            </>
                                        )}
                                    </InfoCard>
                                )}

                                {finalComment && (
                                    <InfoCard title="Analyst's Final Verdict" icon={<BarChart size={16} />}>
                                        <p>{finalComment}</p>
                                    </InfoCard>
                                )}

                                {backupCourseCountry.courseAndCollege && (
                                    <InfoCard title="Recommended Backup Plan" icon={<Shield size={16} />}>
                                        <DetailItem label="Course/College" value={backupCourseCountry.courseAndCollege} />
                                        <DetailItem label="Reasoning" value={backupCourseCountry.reasonSaferPractical} />
                                    </InfoCard>
                                )}

                                {predictedQnA?.length > 0 && (
                                    <InfoCard title="Predicted Interview Q&A" icon={<HelpCircle size={16} />}>
                                        {predictedQnA.map((item, i) => <QnAItem item={item} key={i} />)}
                                    </InfoCard>
                                )}

                            </div>
                        </main>
                        {/* ======================================================= */}
                        {/*                     RIGHT SIDEBAR                       */}
                        {/*         (Focus on Costs, Timelines & Resources)         */}
                        {/* ======================================================= */}
                        <aside className="rm-right-sidebar">
                            <InfoCard title="Cost & Financials" icon={<Wallet size={16} />}>
                                <h4 className="rm-sub-heading">Annual Tuition</h4>
                                <p className="rm-cost-amount">{collegeAnalysis.specificCollegeReport?.tuitionFee?.local} ({collegeAnalysis.specificCollegeReport?.tuitionFee?.inr})</p>
                                <h4 className="rm-sub-heading">Est. Monthly Living Costs</h4>
                                <DetailItem label="Accommodation" value={`${costOfLiving.accommodationMonthly?.local} /mo`} />
                                <DetailItem label="Food & Groceries" value={`${costOfLiving.foodAndGroceryCostMonthly?.local} /mo`} />
                                <DetailItem label="Transport" value={`${costOfLiving.transportcostMonthly?.local} /mo`} />
                                <DetailItem label="Miscellaneous" value={`${costOfLiving.miscellaneousMonthly?.local} /mo`} />
                                <hr />
                                <DetailItem label="Total Monthly" value={`${costOfLiving.cityLivingCostMonthly?.local} /mo`} />
                            </InfoCard>

                            <InfoCard title="PR & Post-Study Pathway" icon={<TrendingUp size={16} />}>
                                <DetailItem label="Post-Study Work Visa" value={postStudyWorkAndPR.postStudyWorkVisaDuration} />
                                <DetailItem label="PR Pathways" value={postStudyWorkAndPR.prPathways} />
                                <h4 className="rm-sub-heading">PR Timeline</h4>
                                <ul className="rm-pr-timeline">{prPathwayRoadmap.timelineSteps?.map(item => (<li key={item.step}><p>{item.step}</p><span>{item.duration}</span></li>))}</ul>
                            </InfoCard>

                            <InfoCard title="Your Next 3 Steps" icon={<ListChecks size={16} />}>
                                <ol className="rm-next-steps-list">{finalActionPlan.next3Steps?.map((step, i) => (<li key={i}>{step}</li>))}</ol>
                            </InfoCard>

                            <InfoCard title="Helpful Resources & Certs" icon={<LinkIcon size={16} />}>
                                <h4 className="rm-sub-heading">Recommended Certifications</h4>
                                <ul>{additionalCertifications.map(cert => <ListItem key={cert.name}><a href={cert.link} target="_blank" rel="noopener noreferrer">{cert.name}</a> ({cert.type})</ListItem>)}</ul>
                                <h4 className="rm-sub-heading">Scholarships</h4>
                                <ul>{scholarshipDetails.map(sch => <ListItem key={sch.name}>{sch.name} ({sch.amount?.local}) - Criteria: {sch.criteria}</ListItem>)}</ul>
                                <h4 className="rm-sub-heading">External Links</h4>
                                <ul>
                                    {footerResources.resumeBuilderLink && <ListItem><a href={footerResources.resumeBuilderLink} target="_blank" rel="noopener noreferrer">Resume Builder</a></ListItem>}
                                    {footerResources.sopGeneratorLink && <ListItem><a href={footerResources.sopGeneratorLink} target="_blank" rel="noopener noreferrer">SOP Generator</a></ListItem>}
                                    {footerResources.budgetCalculatorLink && <ListItem><a href={footerResources.budgetCalculatorLink} target="_blank" rel="noopener noreferrer">Budget Calculator</a></ListItem>}
                                </ul>
                            </InfoCard>
                        </aside>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportModal;

